/**
 * Merges an overlay `field` node with its Dataverse form metadata into one renderable field.
 *
 * Pure and framework-free: this is the one place the two sources of truth meet, so it is
 * tested from fixtures rather than through a rendered form.
 *
 * Precedence is per property, not per source. The overlay owns composition and locking
 * (`required`, `readOnly`, `span`, `hint`); the metadata owns attribute truth (label, option
 * set, length, data type); the caller's vocabulary owns what an attribute *means* on a
 * device, which neither JSON can say.
 *
 * See docs/features/order-capture.md.
 */

import {
  OverlayAutoFill,
  OverlayFieldDefault,
  OverlayLookupView,
  OverlayNode,
} from '@omni/interfaces/app-ui/overlay.interface';
import {
  ScreenControlMetadata,
  ScreenFormMetadata,
  ScreenFormTab,
} from '@omni/interfaces/app-ui/screen-bundle.interface';
import { fetchEntity } from './app-ui-fetch';

/** Dataverse's own language code, used whenever the rep's is not among the translations. */
export const FALLBACK_LANGUAGE_CODE = '1033';

/** How a field is captured. Resolved from the overlay variant, the DataType, or vocabulary. */
export type AppUiFieldVariant =
  /** Free text. */
  | 'text'
  /** Long text. */
  | 'memo'
  /** Option set, chosen from a dropdown. */
  | 'picklist'
  /** A yes/no switch. */
  | 'toggle'
  /** A yes/no box. */
  | 'checkbox'
  /** The record this screen is for. Seeded by the caller; never picked here. */
  | 'customer'
  /** The signed-in user. Seeded by the caller; no offline picker exists. */
  | 'owner'
  /** A status the server owns. Reported, never edited. */
  | 'status'
  /** A reference to another record, chosen from rows synced to the device. */
  | 'lookup'
  /** A number typed in, rounded to `decimals`: whole, decimal. */
  | 'number'
  /** A number in the org's currency, rounded to `decimals`. */
  | 'money'
  /** A calendar date, picked. */
  | 'date'
  /** A date and time, picked as a date at local midnight. */
  | 'datetime'
  /** Several option-set values, picked together. */
  | 'multiselect'
  /** A type this app cannot capture: its value is shown, never an input that drops typing. */
  | 'readonly';

export interface AppUiFieldOption {
  /** An option-set integer, or a lookup's record id. Persisted as-is. */
  value: number | string;
  label: string;
  /** A lookup view's other columns, shown under the label. */
  detail?: string;
  /** Extra text the picker's search matches. */
  search?: string;
}

export interface AppUiFieldModel {
  /** Overlay node id, for tracking and diagnostics. */
  id: string;
  /** Entity attribute. The persistence key and the field's identity. */
  attribute: string;
  label: string;
  /** Helper text from the overlay, if any. */
  hint: string | null;
  variant: AppUiFieldVariant;
  required: boolean;
  /** Never editable by the rep — a derived value, or locked by configuration. */
  readOnly: boolean;
  /** Column span within its section, already clamped to the section's columns. */
  span: number;
  /** Option set for a `picklist`, rows for a `lookup`. Empty means none are on this device. */
  options: AppUiFieldOption[];
  /** For a `lookup`: the table its ids belong to, when configuration or metadata says. */
  lookupEntity: string | null;
  maxLength: number | null;
  /** Places a number keeps: 0 for whole numbers, at most 2 otherwise. */
  decimals: number | null;
  /** True when the metadata came from the synced form rather than a fallback. */
  fromConfig: boolean;
  defaultValue?: OverlayFieldDefault | null;
  autoFill?: OverlayAutoFill | null;
  /** Candidates queried from a view instead of read from the device. */
  lookupView?: OverlayLookupView | null;
  /** The On Change logic this field names. */
  onChangeHandler?: string | null;
  /** The node's rules (and its section's `visibleWhen`), so a gap check applies what the renderer does. */
  when?: AppUiFieldRules;
}

export interface AppUiFieldRules {
  /** Shown only while every one holds. */
  visible: string[];
  required?: string;
  /** Locked while any one holds. */
  locked: string[];
  /** The attribute whose field `reveals` this one: hidden while that value is off. */
  revealedBy?: string;
}

/** A revealing field's value is on: a yes/no holds a real boolean, older configs 1 or 'true'. */
export function isRevealing(value: unknown): boolean {
  return value === true || value === 1 || value === 'true' || value === '1';
}

/** What code knows about one attribute that no configuration can say. */
export interface AppUiAttributeVocabulary {
  /** Fixes how the value is captured, whatever the overlay's own variant says. */
  variant?: AppUiFieldVariant;
  /** i18n key used only when the form metadata carries no label. */
  labelKey?: string;
  /** Used only when no option set is on the device, and only for verified values. */
  fallbackOptions?: { value: number; labelKey: string }[];
}

export interface AppUiMergeOptions {
  /** The parent section's column count, for clamping `span`. */
  sectionColumns: number;
  /** Named rows on the device, for `optionsFrom`. */
  datasets?: Record<string, unknown[]>;
  /** Captured values by attribute, for `optionsFilter.dependsOn`. */
  values?: Record<
    string,
    { value: string | number | boolean | null } | undefined
  >;
  /** Dataverse language code for the rep, e.g. `'1033'`. */
  languageCode?: string;
  /** Resolves an i18n key. Applied to vocabulary labels only; configured text is used as-is. */
  translate: (key: string) => string;
  vocabulary?: Record<string, AppUiAttributeVocabulary>;
}

/** Every control on the form by lower-cased attribute name. Build once, reuse per field. */
export type AppUiControlIndex = Map<string, ScreenControlMetadata>;

/** One field from its overlay node, its form metadata and the caller's vocabulary. */
export function mergeFieldNode(
  node: OverlayNode & { attribute: string },
  control: ScreenControlMetadata | undefined,
  options: AppUiMergeOptions,
): AppUiFieldModel {
  const language = options.languageCode || FALLBACK_LANGUAGE_CODE;
  const vocab = options.vocabulary?.[node.attribute.toLowerCase()] ?? {};
  const variant = vocab.variant ?? resolveVariant(node, control);

  const fieldOptions = resolveFieldOptions(
    node,
    control,
    vocab,
    language,
    options,
  );
  const label = resolveLabel(node, control, vocab, language, options.translate);

  return {
    id: node.id ?? node.attribute,
    attribute: node.attribute,
    label,
    hint: node.hint?.trim() || null,
    variant,
    required: node.required ?? control?.Required === true,
    readOnly: isReadOnlyField(node, control, variant),
    span: Math.min(
      options.sectionColumns,
      Math.max(1, Math.trunc(node.span ?? 1)),
    ),
    options: fieldOptions,
    lookupEntity: variant === 'lookup' ? lookupEntityOf(node, control) : null,
    maxLength: control?.MaxLength ?? null,
    decimals: decimalsOf(node, control, variant),
    fromConfig: !!control,
    defaultValue: node.defaultValue ?? null,
    autoFill: node.autoFill ?? null,
    // A view published by `id` alone is resolved when the picker opens.
    lookupView:
      variant === 'lookup' && (node.lookupView?.fetchXml || node.lookupView?.id)
        ? node.lookupView
        : null,
    onChangeHandler: node.onChange?.handler || null,
  };
}

/** A record identity and a status are locked by what they mean, whatever any source says. */
function isReadOnlyField(
  node: OverlayNode,
  control: ScreenControlMetadata | undefined,
  variant: AppUiFieldVariant,
): boolean {
  return (
    variant === 'customer' ||
    variant === 'status' ||
    variant === 'readonly' ||
    node.variant === 'readonly-badge' ||
    !!node.autoFill?.calculate ||
    (node.readOnly ?? control?.ReadOnly === true)
  );
}

/**
 * Precedence: a dataset the overlay names (the offline-safe lookup), else the metadata's option
 * set — taken as a set, so even an empty one replaces the fallback, since merging two vintages is
 * how a value a tenant retired stays on the device — else the vocabulary's verified fallback.
 * A null `OptionSet` is silence, not "no options".
 */
function resolveFieldOptions(
  node: OverlayNode,
  control: ScreenControlMetadata | undefined,
  vocab: AppUiAttributeVocabulary,
  language: string,
  options: AppUiMergeOptions,
): AppUiFieldOption[] {
  const sourced = node.optionsFrom
    ? optionsFromDataset(node.optionsFrom, options.datasets)
    : null;
  const unfiltered =
    sourced ??
    (control?.OptionSet
      ? readOptions(control, language)
      : (vocab.fallbackOptions ?? []).map((o) => ({
          value: o.value,
          label: options.translate(o.labelKey),
        })));
  return applyOptionsFilter(unfiltered, node.optionsFilter, options.values);
}

/**
 * The overlay's `title` (contract merge rule 3), else metadata's label, else the vocabulary's,
 * else the attribute itself — a caption is never blank.
 */
function resolveLabel(
  node: OverlayNode & { attribute: string },
  control: ScreenControlMetadata | undefined,
  vocab: AppUiAttributeVocabulary,
  language: string,
  translate: (key: string) => string,
): string {
  return (
    node.title?.trim() ||
    pickDisplayName(control?.DisplayNames, language, node.attribute) ||
    (vocab.labelKey ? translate(vocab.labelKey) : '') ||
    node.attribute
  );
}

/** Overlay `field.variant` values this app can present, and what each captures. */
const VARIANT_BY_OVERLAY: Record<string, AppUiFieldVariant> = {
  picklist: 'picklist',
  dropdown: 'picklist',
  segmented: 'picklist',
  chips: 'picklist',
  toggle: 'toggle',
  checkbox: 'checkbox',
  memo: 'memo',
  text: 'text',
  lookup: 'lookup',
};

/** The contract's `appType` (v1.6), decided at publish from the column. It outranks everything but vocabulary. */
const VARIANT_BY_APP_TYPE: Record<string, AppUiFieldVariant> = {
  text: 'text',
  textarea: 'memo',
  whole: 'number',
  decimal: 'number',
  money: 'money',
  date: 'date',
  datetime: 'datetime',
  yesno: 'toggle',
  choice: 'picklist',
  choices: 'multiselect',
  lookup: 'lookup',
};

/** Dataverse types that imply a variant when the overlay names none — configs from before `appType`. */
/* eslint-disable @typescript-eslint/naming-convention -- keys are Dataverse attribute type names */
const VARIANT_BY_DATATYPE: Record<string, AppUiFieldVariant> = {
  PicklistType: 'picklist',
  StatusType: 'picklist',
  StateType: 'picklist',
  BooleanType: 'toggle',
  MemoType: 'memo',
  LookupType: 'lookup',
  IntegerType: 'number',
  BigIntType: 'number',
  DecimalType: 'number',
  DoubleType: 'number',
  MoneyType: 'money',
  DateTimeType: 'date',
  MultiSelectPicklistType: 'multiselect',
};
/* eslint-enable @typescript-eslint/naming-convention */

/** Capture variant for an attribute the vocabulary does not name. */
function resolveVariant(
  node: OverlayNode,
  control: ScreenControlMetadata | undefined,
): AppUiFieldVariant {
  if (typeof node.appType === 'string' && node.appType)
    return VARIANT_BY_APP_TYPE[node.appType] ?? 'readonly';
  const overlayVariant = typeof node.variant === 'string' ? node.variant : '';
  if (
    !VARIANT_BY_OVERLAY[overlayVariant] &&
    control?.DataType === 'DateTimeType' &&
    control.Format === 'DateAndTime'
  )
    return 'datetime';
  return (
    VARIANT_BY_OVERLAY[overlayVariant] ??
    VARIANT_BY_DATATYPE[control?.DataType ?? ''] ??
    'text'
  );
}

/** `decimals` from publish, else the column's precision, else 0 for whole numbers and 2 for the rest. */
function decimalsOf(
  node: OverlayNode,
  control: ScreenControlMetadata | undefined,
  variant: AppUiFieldVariant,
): number | null {
  if (variant !== 'number' && variant !== 'money') return null;
  const published =
    typeof node.decimals === 'number' ? node.decimals : control?.Precision;
  if (typeof published === 'number')
    return Math.min(2, Math.max(0, Math.trunc(published)));
  const whole =
    node.appType === 'whole' ||
    control?.DataType === 'IntegerType' ||
    control?.DataType === 'BigIntType';
  return whole ? 0 : 2;
}

/**
 * The table a lookup points at: configuration first, then metadata when it names exactly one.
 * Polymorphic lookups (`customerid`, `ownerid`) come back with no targets.
 */
function lookupEntityOf(
  node: OverlayNode,
  control: ScreenControlMetadata | undefined,
): string | null {
  if (node.optionsFrom?.entity) return node.optionsFrom.entity;
  const viewEntity = node.lookupView?.fetchXml
    ? fetchEntity(node.lookupView.fetchXml)
    : null;
  if (viewEntity) return viewEntity;
  const targets = (control?.LookupTargets ?? '')
    .split(/[,;]/)
    .map((t) => t.trim())
    .filter(Boolean);
  return targets.length === 1 ? targets[0] : null;
}

/**
 * Options from rows already on the device. Values keep their type: a record id is a GUID
 * string and must not be coerced. Rows missing a value or a label are skipped.
 */
function optionsFromDataset(
  source: { dataset: string; valueAttr: string; labelAttr: string },
  datasets: Record<string, unknown[]> | undefined,
): AppUiFieldOption[] {
  const rows = datasets?.[source.dataset] ?? [];
  const options: AppUiFieldOption[] = [];
  for (const row of rows) {
    const record = row as Record<string, unknown>;
    const raw = record?.[source.valueAttr];
    const label = record?.[source.labelAttr];
    const value =
      typeof raw === 'number'
        ? raw
        : typeof raw === 'string'
          ? raw.trim()
          : null;
    if (
      value === null ||
      value === '' ||
      (typeof value === 'number' && !Number.isFinite(value))
    )
      continue;
    if (label === null || label === undefined || label === '') continue;
    options.push({ value, label: String(label) });
  }
  return options;
}

/**
 * Narrows options by another field's current value. With no dependency value yet, or a value
 * the map does not mention, nothing is narrowed: the map says what a value permits, not what
 * its absence forbids.
 */
function applyOptionsFilter(
  options: AppUiFieldOption[],
  filter:
    | { dependsOn: string; map: Record<string, Array<number | string>> }
    | undefined,
  values: AppUiMergeOptions['values'],
): AppUiFieldOption[] {
  if (!filter?.dependsOn || !filter.map) return options;
  const dependency = values?.[filter.dependsOn]?.value;
  if (dependency === null || dependency === undefined || dependency === '')
    return options;
  const allowed = filter.map[String(dependency)];
  if (!Array.isArray(allowed)) return options;
  // Compared as text: a map read from JSON and a record id can disagree on type, never on value.
  const permitted = new Set(allowed.map(String));
  return options.filter((option) => permitted.has(String(option.value)));
}

/**
 * Every control on the form, by lower-cased attribute name.
 *
 * Both shapes are traversed. On the live order form the flat `Tabs[].Controls` array is empty
 * and the controls sit nested under `Columns[].Sections[].Rows[].Cells[]`; reading only the flat
 * one is the defect that rendered every field "Unsupported" in the other runtime.
 */
export function indexControls(
  metadata: ScreenFormMetadata | null | undefined,
): AppUiControlIndex {
  const byAttribute: AppUiControlIndex = new Map();
  for (const control of everyControl(metadata)) {
    const key = control.AttributeName.toLowerCase();
    // First binding wins, so a field bound on the first tab is not overwritten by the same
    // attribute appearing again later.
    if (!byAttribute.has(key)) byAttribute.set(key, control);
  }
  return byAttribute;
}

/** Both shapes, in document order, skipping controls bound to no attribute. */
function everyControl(
  metadata: ScreenFormMetadata | null | undefined,
): ScreenControlMetadata[] {
  return (metadata?.Tabs ?? [])
    .flatMap((tab) => [...(tab.Controls ?? []), ...nestedControls(tab)])
    .filter((control) => !!control?.AttributeName);
}

/** The nested formxml shape: `Columns[].Sections[].Rows[].Cells[].Control`. */
function nestedControls(tab: ScreenFormTab): ScreenControlMetadata[] {
  return (tab.Columns ?? [])
    .flatMap((column) => column.Sections ?? [])
    .flatMap((section) => section.Rows ?? [])
    .flatMap((row) => row.Cells ?? [])
    .map((cell) => cell.Control)
    .filter((control): control is ScreenControlMetadata => !!control);
}

function readOptions(
  control: ScreenControlMetadata,
  language: string,
): AppUiFieldOption[] {
  const options: AppUiFieldOption[] = [];
  for (const option of control.OptionSet?.Options ?? []) {
    if (option?.Value === null || option?.Value === undefined) continue;
    const label = pickDisplayName(option.DisplayNames, language);
    // An option with no label in any language cannot be presented, and picking by a bare
    // number is not a choice a rep can make.
    if (label) options.push({ value: option.Value, label });
  }
  return options;
}

/** The rep's language, else Dataverse's own, else the first translation present. */
export function pickDisplayName(
  names: // eslint-disable-next-line @typescript-eslint/naming-convention -- Dynamics metadata names its own properties
    | { Description?: string; Label?: string; LanguageCode: string }[]
    | undefined,
  language: string,
  /** A name to pass over: published metadata can list the attribute itself before its label. */
  placeholder?: string,
): string {
  // Dataverse writes the text to `Label` on some rows and `Description` on others.
  const textOf = (n: { Description?: string; Label?: string }): string =>
    n.Label || n.Description || '';
  const usable = (names ?? []).filter(
    (n) =>
      !!textOf(n) && textOf(n).toLowerCase() !== placeholder?.toLowerCase(),
  );
  const match =
    usable.find((n) => String(n.LanguageCode) === language) ??
    usable.find((n) => String(n.LanguageCode) === FALLBACK_LANGUAGE_CODE) ??
    usable[0];
  return match ? textOf(match) : '';
}
